import { useMemo, useState } from "react";
import { useFetch } from "@/lib/content";
import { useSEO } from "@/lib/seo";
import { Eyebrow, Reveal, ParallaxImage } from "@/components/site/Motion";
import CaseCard from "@/components/site/CaseCard";

const HERO_IMG =
  "https://images.unsplash.com/photo-1582057811341-a22d524c6a4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-obsidian/20 bg-paper px-4 py-3 text-sm outline-none transition-colors focus:border-progress"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function CaseStudies() {
  const { data: cases } = useFetch("/case-studies");
  const { data: services } = useFetch("/services");

  const [industry, setIndustry] = useState("");
  const [series, setSeries] = useState("");
  const [size, setSize] = useState("");

  useSEO({
    title: "Case Studies | RenB Solutions",
    description: "Structured RenB case studies — context, problem, approach, outcome and metrics — across IT, healthcare, manufacturing and technology services for SMEs.",
    path: "/case-studies",
  });

  const industries = useMemo(() => [...new Set((cases || []).map((c) => c.industry).filter(Boolean))].sort(), [cases]);
  const sizes = useMemo(() => [...new Set((cases || []).map((c) => c.company_size).filter(Boolean))], [cases]);

  const filtered = (cases || []).filter(
    (c) =>
      (!industry || c.industry === industry) &&
      (!series || c.series_code === series) &&
      (!size || c.company_size === size)
  );

  const hasFilter = industry || series || size;

  return (
    <div data-testid="case-studies-page">
      <section className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-28 md:px-10 md:pb-20 md:pt-32 lg:grid-cols-12 lg:items-stretch lg:gap-8">
          <Reveal className="lg:col-span-7 lg:self-center">
            <Eyebrow>Case studies</Eyebrow>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-7xl">
              Proof, in the fixed structure of a magazine feature<span className="text-progress">.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-obsidian/70 md:text-lg">
              Every RenB case study reads the same way — Context, Problem, Approach, Outcome, Metrics — so you
              can judge the work, not the adjectives.
            </p>
          </Reveal>
          <div className="hidden lg:col-span-5 lg:block">
            <ParallaxImage
              src={HERO_IMG}
              alt="Drafting table with plans laid out"
              className="h-full min-h-[460px] border border-ash"
              imgClassName="grayscale"
            />
          </div>
        </div>
      </section>

      <section className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-6 px-5 py-10 sm:grid-cols-3 md:px-10">
          <FilterSelect label="Industry" value={industry} onChange={setIndustry} options={industries} testid="filter-industry" />
          <FilterSelect label="Service series" value={series} onChange={setSeries} options={(services || []).map((s) => s.series_code)} testid="filter-series" />
          <FilterSelect label="Company size" value={size} onChange={setSize} options={sizes} testid="filter-size" />
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
        <p data-testid="case-count" className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">
          {filtered.length} {filtered.length === 1 ? "case study" : "case studies"}
          {hasFilter && (
            <button
              data-testid="clear-filters"
              onClick={() => {
                setIndustry("");
                setSeries("");
                setSize("");
              }}
              className="ml-4 text-progress underline underline-offset-4"
            >
              Clear filters
            </button>
          )}
        </p>
        {filtered.length === 0 ? (
          <div className="border border-ash p-16 text-center" data-testid="case-empty-state">
            <p className="font-serif text-3xl">No case studies match these filters yet.</p>
            <p className="mt-3 text-sm text-slategray">Try clearing a filter — or ask us about engagements in your industry.</p>
          </div>
        ) : (
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {filtered.map((c, i) => (
              <Reveal key={c.id} delay={Math.min(i * 0.06, 0.2)}>
                <CaseCard item={c} index={i} />
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
